<template>
  <component
    :is="selectedHeadingType"
    ref="title"
    data-testid="title-form"
    class="tw-outline-none tw-flex"
    contenteditable
    @keydown.enter="handleDismiss"
    @keydown.esc="handleEsc"
  >
    {{ contentValue }}
  </component>

  <SfDropdown
    trigger-size="large"
    :dropdown-id="`title-size-dropdown-${title.id}`"
    :is-open="titleSizeDropdownOpen"
    has-actions="true"
    @dropdown-trigger-clicked="handleToggleDropdown(title.id)"
    @dropdown-closed="handleCloseDropdown"
  >
    <template #trigger>{{ sizeDropdownLabel }}</template>
    <template #actions>
      <div
        data-testid="title-form-remove"
        class="
          sf-dropdown2-trash
          tw-flex
          tw-items-center
          tw-border
          tw-border-l-0
          tw-border-r-0
          tw-border-solid
          tw-border-sf-neutral-lighter-2
          tw-bg-sf-snow-lighter
          tw-text-sf-neutral-light-2
          tw-px-3
          hover:tw-bg-sf-snow hover:tw-text-sf-neutral
        "
        @click.stop.prevent="handleRemove"
      >
        <i class="fa fa-trash" />
      </div>
      <div
        data-testid="title-form-accept"
        class="
          sf-dropdown2-dismiss
          tw-flex
          tw-items-center
          tw-border
          tw-border-l-0
          tw-border-solid
          tw-border-sf-neutral-lighter-2
          tw-bg-sf-snow-lighter
          tw-text-sf-neutral-light-2
          tw-px-3
          hover:tw-bg-sf-snow hover:tw-text-sf-neutral
        "
        @click.stop.prevent="handleDismiss"
      >
        <i class="fa fa-check" />
      </div>
    </template>
    <template #menu>
      <SfDropdownItem
        :has-checkmark="selectedHeadingType === 'h3'"
        @click="handleSelect('h3')"
      >
        <span class="tw-text-lg">
          {{ copy.heading3 }}
        </span>
      </SfDropdownItem>
      <SfDropdownItem
        :has-checkmark="selectedHeadingType === 'h2'"
        @click="handleSelect('h2')"
      >
        <span class="tw-text-xl">
          {{ copy.heading2 }}
        </span>
      </SfDropdownItem>
      <SfDropdownItem
        :has-checkmark="selectedHeadingType === 'h1'"
        @click="handleSelect('h1')"
      >
        <span class="tw-text-2xl">
          {{ copy.heading1 }}
        </span>
      </SfDropdownItem>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useTemplateRef, nextTick } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'

import type { Title } from '@app/application/communication/types'

import {
  SfDropdown,
  SfDropdownItem,
} from '@app/shared/vue/components/sf-dropdown'

interface Props {
  title: Title
}

const props = withDefaults(defineProps<Props>(), {})

const emit =
  defineEmits<{
    dismissed: []
    updateTitle: [{ id?: number; content: string; size: string }]
    removeTitle: [{ id: number }]
  }>()

const I18n = useI18n()
const copy = {
  heading3: I18n.t('client_meetings.prepare.heading_3'),
  heading2: I18n.t('client_meetings.prepare.heading_2'),
  heading1: I18n.t('client_meetings.prepare.heading_1'),
}

const sizeDropdownLabel = computed(() => {
  if (selectedHeadingType.value === 'h3') {
    return copy.heading3
  } else if (selectedHeadingType.value === 'h2') {
    return copy.heading2
  } else if (selectedHeadingType.value === 'h1') {
    return copy.heading1
  } else {
    return I18n.t('client_meetings.prepare.select_size')
  }
})

const originalContent = props.title.content
const originalSize = props.title.size || 'h3'

const contentValue = ref(props.title.content)
const titleRef = useTemplateRef<HTMLElement>('title')
const currentDropdownId = ref('')
const selectedHeadingType = ref(props.title.size || 'h3')
const trimmedTitle = computed(() =>
  (titleRef.value?.innerText || '').replace(/[\n\r]*/gm, '').trim()
)

const titleSizeDropdownOpen = computed(
  () => currentDropdownId.value === `title-size-dropdown-${props.title.id}`
)

function updateTitle(id: number, content: string, size: string) {
  emit('updateTitle', { id, content, size })
}

function handleSelect(size: string) {
  contentValue.value = trimmedTitle.value
  selectedHeadingType.value = size
  handleCloseDropdown()
  focusEndOfContent()
}

function handleEsc(event: KeyboardEvent) {
  event.preventDefault()
  if (titleRef.value) {
    titleRef.value.innerText = originalContent
  }
  handleDismiss()
}

function handleDismiss() {
  const newContent = trimmedTitle.value
  if (
    newContent !== originalContent ||
    selectedHeadingType.value !== originalSize
  ) {
    updateTitle(props.title.id, newContent, selectedHeadingType.value)
  }

  emit('dismissed')
  handleCloseDropdown()
}

function handleRemove() {
  emit('removeTitle', { id: props.title.id })
  emit('dismissed')
  handleCloseDropdown()
}

function handleCloseDropdown() {
  currentDropdownId.value = ''
}

function handleToggleDropdown(id: number) {
  if (currentDropdownId.value === `title-size-dropdown-${id}`) {
    currentDropdownId.value = ''
    return
  }

  currentDropdownId.value = `title-size-dropdown-${id}`
}

function focusEndOfContent() {
  // Use nextTick to ensure DOM is updated before focusing
  // as we use the ref on the dynamic <component> tag.
  nextTick(() => {
    if (titleRef.value) {
      titleRef.value.focus()
      window.getSelection()?.selectAllChildren(titleRef.value)
      window.getSelection()?.collapseToEnd()
    }
  })
}

onMounted(() => {
  contentValue.value = props.title.content
  if (titleRef.value) {
    titleRef.value.innerText = contentValue.value
  }

  focusEndOfContent()
})
</script>
